import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import TopNav from "../components/TopNav";
import Footer from "../components/Footer";
import API from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../contexts/AuthContext";
import { SubmissionWizard } from "../components/SubmissionWizard";
import { Plus, FileText, ArrowRight, Bell, Clock } from "lucide-react";
import logger from "../lib/logger";

export function MemberDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [d, s] = await Promise.all([API.get("/dashboard"), API.get("/submissions", { params: { mine: true } })]);
        setStats(d.data); setSubs((s.data.items || []).slice(0, 6));
      } catch (e) { logger.error("Member dashboard load failed:", e); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-10 flex-1 w-full">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-h mb-2">Community Dashboard</div>
            <h1 className="font-display text-4xl text-ink">Welcome, {user?.first_name}</h1>
            <p className="text-ink-soft mt-1">Your Profile ID: <span className="font-mono text-ink">{user?.profile_id}</span></p>
          </div>
          <Link to="/member/submissions/new" className="btn-primary" data-testid="member-new-submission">
            <Plus size={16} /> New Submission
          </Link>
        </div>

        {!user?.email_verified && (
          <div className="mb-6 bg-[var(--heritage-parchment)] border-l-4 border-[var(--heritage-gold)] p-4 rounded-r flex items-center justify-between gap-4" data-testid="verify-banner">
            <div className="text-sm text-ink-soft">
              <strong className="text-ink">Please verify your email.</strong> Some features require a verified address.
            </div>
            <button onClick={async () => {
              try {
                await API.post("/auth/resend-verification");
                alert("Verification email sent.");
              } catch (e) {
                logger.error("Resend verification failed:", e);
                alert("Could not resend verification email.");
              }
            }}
                    className="btn-outline text-sm" data-testid="resend-verify">Resend Email</button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="My Submissions" value={stats.my_submissions ?? "–"} to="/member/submissions" testid="stat-my-subs" />
          <StatCard label="Drafts" value={stats.my_drafts ?? "–"} to="/member/submissions?status=draft" testid="stat-drafts" />
          <StatCard label="Action Required" value={stats.action_required ?? "–"} to="/member/tasks" accent testid="stat-actions" />
          <StatCard label="Notifications" value={stats.unread_notifications ?? "–"} to="/notifications" icon={<Bell size={16} />} testid="stat-notif" />
        </div>

        <section className="mb-8">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl text-ink">Recent Submissions</h2>
            <Link to="/member/submissions" className="text-sm text-terracotta hover:underline">View all →</Link>
          </div>
          {subs.length === 0 ? (
            <div className="card-heritage text-center py-10">
              <FileText size={32} className="mx-auto text-muted-h mb-3" />
              <div className="font-display text-xl text-ink mb-1">Start your first submission</div>
              <p className="text-ink-soft text-sm mb-4">Share information about a heritage item held by your family or community.</p>
              <Link to="/member/submissions/new" className="btn-primary inline-flex" data-testid="empty-new-sub">Create Submission</Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {subs.map((s) => (
                <Link key={s.id} to={`/member/submissions/${s.id}`} className="card-heritage hover:shadow-md hover:-translate-y-0.5 transition" data-testid={`member-sub-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-display text-lg text-ink line-clamp-1">{s.item?.title || "Untitled"}</div>
                      <div className="text-xs text-muted-h mt-1 flex items-center gap-1"><Clock size={12} /> Updated {new Date(s.updated_at).toLocaleDateString()}</div>
                    </div>
                    <StatusBadge status={s.status} memberView />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
}

function StatCard({ label, value, to, icon, accent, testid }) {
  return (
    <Link to={to} className={`card-heritage flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition ${accent && value > 0 ? "!border-[var(--heritage-terracotta)] bg-[var(--heritage-white)]" : ""}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-h flex items-center gap-1">{icon}{label}</div>
      <div className={`font-display text-4xl mt-2 ${accent && value > 0 ? "text-terracotta" : "text-ink"}`}>{value}</div>
    </Link>
  );
}

export function NewSubmission() {
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-4xl mx-auto px-6 py-10 flex-1 w-full">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-muted-h mb-2">New Submission</div>
          <h1 className="font-display text-4xl text-ink">Share a Heritage Item</h1>
          <p className="text-ink-soft mt-2">You can save your progress at any time and return later.</p>
        </div>
        <SubmissionWizard mode="authenticated" />
      </div>
      <Footer />
    </div>
  );
}

export function MySubmissions() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => {
    try { const { data } = await API.get("/submissions", { params: { mine: true } }); setItems(data.items || []); }
    catch (e) { logger.error("My submissions load failed:", e); }
  })(); }, []);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-10 flex-1 w-full">
        <div className="flex items-end justify-between mb-6">
          <h1 className="font-display text-4xl text-ink">My Submissions</h1>
          <Link to="/member/submissions/new" className="btn-primary"><Plus size={16} /> New</Link>
        </div>
        {items.length === 0 ? (
          <div className="card-heritage text-center py-16">
            <div className="font-display text-xl text-ink">Nothing here yet</div>
            <p className="text-ink-soft mt-2">Create your first submission to get started.</p>
          </div>
        ) : (
          <div className="card-heritage p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--heritage-parchment)] text-left text-xs uppercase tracking-wider text-muted-h">
                <tr>
                  <th className="px-4 py-3">Item</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Updated</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-t border-heritage hover:bg-[var(--heritage-cream)]" data-testid={`row-sub-${s.id}`}>
                    <td className="px-4 py-3 font-medium text-ink">{s.item?.title || "Untitled"}</td>
                    <td className="px-4 py-3"><StatusBadge status={s.status} memberView /></td>
                    <td className="px-4 py-3 text-muted-h">{new Date(s.updated_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right"><Link to={`/member/submissions/${s.id}`} className="text-terracotta hover:underline">View <ArrowRight className="inline" size={14} /></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
